import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

type FormatDateType = 'YYYY-MM-DD' | 'YYYY-MM' | 'MM-DD' | 'YYYY/MM/DD' | 'YYYY/MM' | 'MM/DD' | 'MM'
type Direction = 'forward' | 'backward'

// 啟用插件
dayjs.extend(utc)
dayjs.extend(timezone)

/** 處理日期相關資訊 */
export const useDate = () => {
  // Get the current date using dayjs
  const date = dayjs()
  const year = date.year()
  const month = date.month() + 1 // `month()` is zero-indexed in dayjs
  const day = date.date()

  const formatDate = (
    inputDate: string = dayjs().format(),
    type: FormatDateType = 'YYYY-MM-DD'
  ) => {
    const date = dayjs(inputDate)

    if (!date.isValid()) {
      throw new Error('Invalid date provided')
    }

    // Use the `format` method from dayjs to format the date
    switch (type) {
      case 'YYYY-MM-DD':
        return date.format('YYYY-MM-DD')
      case 'YYYY-MM':
        return date.format('YYYY-MM')
      case 'MM-DD':
        return date.format('MM-DD')
      case 'YYYY/MM/DD':
        return date.format('YYYY/MM/DD')
      case 'YYYY/MM':
        return date.format('YYYY/MM')
      case 'MM/DD':
        return date.format('MM/DD')
      case 'MM':
        return date.format('MM')
      default:
        return ''
    }
  }

  // A generalized function to calculate the date forward or backward using dayjs
  const calculateDate = (
    startDate: string | dayjs.Dayjs,
    direction: Direction = 'backward',
    days: number,
    type: FormatDateType = 'YYYY-MM-DD'
  ) => {
    let date = typeof startDate === 'string' ? dayjs(startDate) : startDate

    if (!date.isValid()) {
      throw new Error('Invalid date provided')
    }

    // Include the start date in the calculation
    date = direction === 'backward' ? date.subtract(days - 1, 'day') : date.add(days - 1, 'day')

    return date.format(type)
  }

  const today = () => {
    return formatDate()
  }

  /**
   * Retrieves the current date and time formatted as 'YYYY-MM-DD HH:mm:ss'.
   * @returns {string} The current date and time in the specified format.
   */
  function getCurrentDateTime(): string {
    return dayjs().format('YYYY-MM-DD HH:mm:ss')
  }

  function getTargetDateTime(time: string): string {
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
  }

  function getEventDateTime(time: string): string {
    return dayjs(time).format('MM-DD HH:mm')
  }

  const getDaysInTwoMonths = (): number => {
    return new Date(year, month - 1, 0).getDate() // 以startDate月分為主
  }

  /**
   * 篩選彈窗快捷選項（今日 / 近 2 日 / 近 3 日 / 近 7 日 / 近一個月）的日期區間。
   *
   * 新系統以「目前帳務日」為基準（OP#2730）：結束點是該帳務日的上限（起點 + 1 天），
   * 起點再往回推 days-1 天，所以「今日」剛好是完整的一個帳務日。結帳點還沒到時整段
   * 區間會退一天，才不會超出後端上限（見 `getAccountingPeriod`）。小時由呼叫端另外
   * 帶入結算時間。
   *
   * 舊系統沒有結帳時間概念、只送純日期，維持原本的 today-(days-1) ~ today。
   *
   * @param cutoff 結帳時間 `HH:mm`，僅新系統使用
   * @param timeZoneId 店家時區（IANA），僅新系統使用
   */
  const getQuickRangeDate = (
    days: number,
    enableHourFilter: boolean,
    cutoff = '00:00',
    timeZoneId = ''
  ) => {
    if (!enableHourFilter) {
      const base = today()
      return { startDate: calculateDate(base, 'backward', days), endDate: base }
    }

    const { periodStart, upperBound } = getAccountingPeriod(cutoff, timeZoneId)

    return {
      startDate: dayjs(periodStart)
        .subtract(days - 1, 'day')
        .format('YYYY-MM-DD'),
      endDate: dayjs(upperBound).format('YYYY-MM-DD')
    }
  }

  /**
   * 套用的區間是不是「今日」這個快捷選項（`getQuickRangeDate(1, ...)`）。
   *
   * 只有一個帳務日時長條圖只會有一根長條，畫面要退回未篩選時的摘要板。判斷直接跟快捷
   * 選項同源，才不會跟篩選彈窗各自定義一次「今日」。小時只在新系統（有結帳時間）時比對。
   */
  const isTodayQuickRange = (
    range: { startDate: string; endDate: string; startTime?: string; endTime?: string },
    enableHourFilter: boolean,
    cutoff = '00:00',
    timeZoneId = ''
  ): boolean => {
    const todayRange = getQuickRangeDate(1, enableHourFilter, cutoff, timeZoneId)
    if (range.startDate !== todayRange.startDate || range.endDate !== todayRange.endDate)
      return false
    return !enableHourFilter || (range.startTime === cutoff && range.endTime === cutoff)
  }

  /**
   * 目前所在的帳務日（OP#2730）。
   *
   * 後端 `AccountingModulesV2.ResolveWindows` 的查詢結束時間上限是「現在以前最近的一個
   * 結帳點 + 24 小時」，日期以店家時區為準。前端若固定用「今天結帳點 + 1 天」當上限，
   * 在還沒到今天的結帳點時會比後端晚整整一天，查詢直接被擋下回 400。
   *
   * @param cutoff 結帳時間，`HH:mm`
   * @param timeZoneId 店家時區（IANA）。空字串時退回瀏覽器時區——時區資料尚未載入完成時
   *   會是空字串，`dayjs.tz('')` 會拋 RangeError
   * @returns `periodStart` 目前帳務日的起點、`upperBound` 可查詢的結束時間上限，
   *   皆為 `YYYY-MM-DD HH:mm`
   */
  const getAccountingPeriod = (cutoff: string, timeZoneId: string) => {
    const now = timeZoneId ? dayjs().tz(timeZoneId) : dayjs()
    // 結帳點本身也要在店家時區建構：用瀏覽器時區建出來的同一串 'YYYY-MM-DD HH:mm'
    // 代表的是不同的絕對時刻，跨時區比較會差一天
    const todayCutoffText = `${now.format('YYYY-MM-DD')} ${cutoff}`
    const todayCutoff = timeZoneId ? dayjs.tz(todayCutoffText, timeZoneId) : dayjs(todayCutoffText)
    const periodStart = now.isBefore(todayCutoff) ? todayCutoff.subtract(1, 'day') : todayCutoff

    return {
      periodStart: periodStart.format('YYYY-MM-DD HH:mm'),
      upperBound: periodStart.add(1, 'day').format('YYYY-MM-DD HH:mm')
    }
  }

  /**
   * Calculate the date for three months ago, considering different month lengths.
   * @returns {string} The date formatted as 'YYYY-MM-DD'
   */
  const getThreeMonthsAgo = (): string => {
    return dayjs().subtract(3, 'month').format('YYYY-MM-DD')
  }

  const getMonth = (date: string | null) => {
    if (!date) return ''
    return dayjs(date).format('MM')
  }

  const getDayOfWeek = (date: string | null) => {
    return dayjs(date).day()
  }

  const getFirstDayOfThisWeek = () => {
    // 使用 en-us 的 locale 來獲取當前日期的第一天
    // 這裡的第一天是指一周的第一天，通常是星期日(en-us)或星期一(en-eu)
    return dayjs().locale('en-us').startOf('week').format('YYYY-MM-DD')
  }

  const getEndDayOfThisWeek = () => {
    // 使用 en-us 的 locale 來獲取當前日期的最後一天
    // 這裡的最後一天是指一周的最後一天，通常是星期六(en-us)或星期日(en-eu)
    return dayjs().locale('en-us').endOf('week').format('YYYY-MM-DD')
  }

  const getFirstDayOfThisMonth = () => {
    return dayjs().startOf('month').format('YYYY-MM-DD')
  }

  /**
   * 獲取下一天的日期
   * @param date 日期字串
   * @returns {string} 下一天的日期
   */
  const getNextDate = (date: string | null) => {
    if (!date) return ''
    return dayjs(date).add(1, 'day').format('YYYY-MM-DD')
  }

  /**
   * 判斷日期是否在當月
   * @param date 日期字串
   * @returns {boolean} 是否在當月
   */
  const isDateInThisMonth = (date: string | null) => {
    if (!date) return false
    return dayjs().isSame(dayjs(date), 'month')
  }

  /**
   * 判斷日期是否在本週
   * @param date 日期字串
   * @returns {boolean} 是否在本週
   */
  const isDateInThisWeek = (date: string | null) => {
    if (!date) return false
    return dayjs().isSame(dayjs(date), 'week')
  }

  /**
   * 獲取兩個日期中較早的日期
   * @param date1 日期字串
   * @param date2 日期字串
   * @returns {day} 較早的日期
   */
  const getTheDayBefore = (date1: string | null, date2: string | null): string => {
    if (!date1 || !date2) return 'please input date'

    const day1 = dayjs(date1)
    const day2 = dayjs(date2)

    return day1.isBefore(day2) ? day1.format('YYYY-MM-DD') : day2.format('YYYY-MM-DD')
  }

  /**
   * 獲取兩個日期之間的天數差
   * @param startDate 日期字串
   * @param endDate 日期字串
   * @returns {number} 天數差
   */
  const getDifferenceInDays = (startDate: string, endDate: string): number => {
    return dayjs(endDate).diff(dayjs(startDate), 'day')
  }

  /**
   * 判斷兩個日期是否相同
   * @param date1 日期字串
   * @param date2 日期字串
   * @returns {boolean} 是否相同
   */
  const isSameDate = (date1: string, date2: string): boolean => {
    return dayjs(date1).isSame(dayjs(date2), 'day')
  }

  /**
   * 獲取幾天後的日期
   * @param startDate 起始日期
   * @param days 天數
   * @returns {string} 幾天後的日期
   */
  const getTheDateInAFewDays = (startDate: string, days: number): string => {
    return dayjs(startDate).add(days, 'day').format('YYYY-MM-DD')
  }

  /**
   * 判斷今日是否落在兩個日期之間 (包含起始日和結束日)
   * @param {string} startDate - 起始日期
   * @param {string} endDate - 結束日期
   * @returns {boolean} - 如果今日落在範圍內，則回傳 true，否則回傳 false
   */
  const isTodayBetweenDates = (startDate: string, endDate: string): boolean => {
    const today = dayjs() // 取得今天的日期，時間會設定為當前時間
    // 將輸入的日期轉換為 Day.js 物件
    const start = dayjs(startDate).startOf('day') // 將起始日期設定為當天開始 (00:00:00)
    const end = dayjs(endDate).endOf('day') // 將結束日期設定為當天結束 (23:59:59)

    // 判斷今天是否在起始日期之後或等於起始日期，並且在結束日期之前或等於結束日期
    return today.isAfter(start) && today.isBefore(end)
  }

  const isAfterTheDate = (date: string) => {
    return dayjs().isAfter(dayjs(date))
  }

  const getDateByTimezone = (date: string, timezone: string): string => {
    // timezone 尚未載入完成時（如剛登入、使用者資訊 API 尚未回應）會是空字串，
    // dayjs.tz('') 對此會拋出 RangeError，改以瀏覽器本地時區顯示，避免頁面當機
    if (!timezone) {
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    }
    return dayjs(date).tz(timezone).format('YYYY-MM-DD HH:mm:ss')
  }

  const isSundayOrSaturday = (date: string): boolean => {
    const dayOfWeek = dayjs(date).day()
    return dayOfWeek === 0 || dayOfWeek === 6
  }

  return {
    year,
    month,
    day,
    formatDate,
    calculateDate,
    today,
    getCurrentDateTime,
    getTargetDateTime,
    getEventDateTime,
    getDaysInTwoMonths,
    getQuickRangeDate,
    isTodayQuickRange,
    getAccountingPeriod,
    getThreeMonthsAgo,
    getMonth,
    getDayOfWeek,
    getFirstDayOfThisWeek,
    getEndDayOfThisWeek,
    getFirstDayOfThisMonth,
    getNextDate,
    isDateInThisMonth,
    isDateInThisWeek,
    getTheDayBefore,
    getDifferenceInDays,
    isSameDate,
    getTheDateInAFewDays,
    isTodayBetweenDates,
    isAfterTheDate,
    getDateByTimezone,
    isSundayOrSaturday
  }
}
